import { TutorialDefinition } from '../TutorialEngine.js';
import { DelugeRenderOptions } from './DelugeTutorialAdapter.js';
import { COLORS } from '../../rendering/Renderer.js';

type DelugeScene = {
    gameState: string;
    renderOptions: DelugeRenderOptions;
    annotations: TutorialDefinition['scenes'][number]['annotations'];
    animate?: {
        interval: number;
        loop: boolean;
        frames: string[];
    };
};

// Grid: 6/1-3:1D1,2A2,3E3
// Digit 1 at D1 (col 3, row 1)
// Digit 2 at A2 (col 0, row 2)
// Digit 3 at E3 (col 4, row 3)
// Full solution: D,E1fD2fC3fC2bB1bC4bC5f

const scenes: DelugeScene[] = [
    // Scene 1: Goal — what a solved puzzle looks like
    {
        gameState: 'D,E1fD2fC3fC2bB1bC4bC5f',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'In Deluge, fill all digits\nin order with water', cell: 'C4', anchor: 'center', color: COLORS.teal },
        ],
    },

    // Scene 2: Empty room — progressive fill animation, quadrants and badge colors
    {
        gameState: 'C@24',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Water flows down\nfrom the faucet', cell: 'C0', anchor: 'near-right' },
            { type: 'text-label', text: 'Each cell has\n4 quadrants', cell: 'E5', anchor: 'center' },
            { type: 'text-label', text: 'Teal badge =\ncorrect order', cell: 'C2', anchor: 'center', color: COLORS.teal },
            { type: 'text-label', text: 'Amber badge =\nincorrect order', cell: 'D4', anchor: 'center', color: COLORS.amber },
        ],
        animate: {
            interval: 2000,
            loop: true,
            frames: [
                'C@24',
                'C@48',
                'C@72',
                'C@96',
                'C@120',
                'C',
            ],
        },
    },

    // Scene 3: Placing walls, moving the faucet
    {
        gameState: 'C,E5bD4b',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Click top edge\nto move faucet', cell: 'B1', anchor: 'center' },
            { type: 'text-label', text: 'Click cells to\ncycle wall types', cell: 'B3', anchor: 'center' },
            { type: 'pulse', edge: '3040', color: COLORS.teal },
            { type: 'pulse', cell: 'D3', color: COLORS.teal },
        ],
        animate: {
            interval: 2000,
            loop: true,
            frames: [
                'C,E5bD4b',
                'D,E5bD4b',
                'D,E5bD4bD3f',
                'D,E5bD4bD3b',
                'D,E5bD4bD3x',
            ],
        },
    },

    // Scene 4: Basic overflow split — water divides around walls
    {
        gameState: 'D,E5bD4x',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Water splits\naround walls', cell: 'C2', anchor: 'center' },
        ],
        animate: {
            interval: 2000,
            loop: true,
            frames: [
                'D,E5bD4x@1',
                'D,E5bD4x@13',
                'D,E5bD4x@31',
                'D,E5bD4x@34',
                'D,E5bD4x@48',
                'D,E5bD4x@72',
                'D,E5bD4x',
            ],
        },
    },

    // Scene 5: Siphon with overflow — water rises over walls
    {
        gameState: 'A,C0fB1fB2bC2fE2fD3fC4fB5f',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Water rises\nand overflows walls', cell: 'C4', anchor: 'center' },
        ],
        animate: {
            interval: 2000,
            loop: true,
            frames: [
                'A,C0fB1fB2bC2fE2fD3fC4fB5f@16',
                'A,C0fB1fB2bC2fE2fD3fC4fB5f@30',
                'A,C0fB1fB2bC2fE2fD3fC4fB5f@44',
                'A,C0fB1fB2bC2fE2fD3fC4fB5f@52',
                'A,C0fB1fB2bC2fE2fD3fC4fB5f@66',
                'A,C0fB1fB2bC2fE2fD3fC4fB5f',
            ],
        },
    },

    // Scene 6: Flow block (sealed area) and disconnected walls
    {
        gameState: 'A,B1fB2bC2fE2fD3fC4fB5fC0fD2b',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Walls cannot\ncreate sealed areas', cell: 'D5', anchor: 'center', color: COLORS.amber },
            { type: 'text-label', text: 'Walls must connect\nto the grid edge', cell: 'E0', anchor: 'center', color: COLORS.amber },
        ],
        animate: {
            interval: 3000,
            loop: true,
            frames: [
                'A,B1fB2bC2fE2fD3fC4fB5fC0fD2b',
                'A,B1fB2bC2fE2fD3fC4fB5f',
            ],
        },
    },

];

/**
 * Deluge tutorial: 6 scenes on a 6x6 grid with digits 1-3.
 * See docs/tutorials/deluge.md for detailed scene descriptions.
 */
export const DELUGE_TUTORIAL: TutorialDefinition = {
    gameType: 'deluge',
    gridCode: '6/1-3:1D1,2A2,3E3',
    scenes,
};
